// Global settings cache to prevent repeated system_settings queries
import { supabase } from '@/integrations/supabase/client';

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  promise?: Promise<T>;
}

class SettingsCache {
  private cache = new Map<string, CacheEntry<any>>();
  private readonly ttl = 10 * 60 * 1000; // 10 minutes TTL

  async getSetting<T = string>(
    key: string,
    defaultValue?: T,
    ttl?: number
  ): Promise<T> {
    const now = Date.now();
    const effectiveTtl = ttl || this.ttl;
    const cached = this.cache.get(key);

    // Return cached value if still valid
    if (cached && now - cached.timestamp < effectiveTtl) {
      if (cached.promise) {
        // console.log(`[SettingsCache] Using in-flight request for: ${key}`);
        return cached.promise;
      }
      // console.log(`[SettingsCache] Using cached value for: ${key}`);
      return cached.data;
    }

    // Create new request
    // console.log(`[SettingsCache] Fetching setting: ${key}`);
    const promise = this.fetchSetting<T>(key, defaultValue);

    this.cache.set(key, {
      data: defaultValue as T,
      timestamp: now,
      promise,
    });

    try {
      const result = await promise;
      this.cache.set(key, {
        data: result,
        timestamp: now,
      });
      return result;
    } catch (error) {
      // Remove failed request from cache
      this.cache.delete(key);
      throw error;
    }
  }

  private async fetchSetting<T>(key: string, defaultValue?: T): Promise<T> {
    const { data, error } = await supabase
      .from('system_settings')
      .select('setting_value, setting_type')
      .eq('setting_key', key)
      .maybeSingle();

    if (error) throw error;

    if (!data) {
      return defaultValue as T;
    }

    // Parse based on setting_type
    switch (data.setting_type) {
      case 'boolean':
        return (data.setting_value === 'true') as T;
      case 'number':
        return Number(data.setting_value) as T;
      case 'json':
        return JSON.parse(data.setting_value || '{}') as T;
      default:
        return data.setting_value as T;
    }
  }

  // Clear cache for specific key
  clearKey(key: string) {
    console.log(`[SettingsCache] Clearing key: ${key}`);
    this.cache.delete(key);
  }

  // Clear all cache
  clear() {
    // console.log('[SettingsCache] Clearing all cache');
    this.cache.clear();
  }

  // Get cache stats for debugging
  getStats() {
    const now = Date.now();
    const active = Array.from(this.cache.entries()).filter(
      ([_, entry]) => now - entry.timestamp < this.ttl
    );

    return {
      totalEntries: this.cache.size,
      activeEntries: active.length,
      oldEntries: this.cache.size - active.length,
    };
  }
}

export const settingsCache = new SettingsCache();

// Cleanup old entries periodically
setInterval(() => {
  const now = Date.now();
  for (const [key, entry] of settingsCache['cache']) {
    if (now - entry.timestamp > 30 * 60 * 1000) {
      // 30 minutes
      settingsCache['cache'].delete(key);
    }
  }
}, 15 * 60 * 1000); // Every 15 minutes
